import React, { useEffect } from 'react';
import { useFormikContext } from 'formik';
import classNames from 'classnames';
import FormTextField from './FormTextField';
import { FormTextAreaField } from './FormTextAreaField';
import { FormBooleanField } from './FormBooleanField';
import FormDateTimeField from './FormDateTimeField';
import FormSingleSelectField from './FormSingleSelectField';
import FormMultiSelectField from './FormMultiSelectField';

interface FieldMetaData {
    name: any;
    label: string;
    type: string;
    bRequired?: boolean;
    toolTip?: string;
}

interface TooltipIconData {
    mainIcon: string;
    closeIcon: string;
}

interface FormBuilderProps<T> {
    fieldRenderers?: any;
    overrideFormFieldRenderer?: any;
    data?: {
        metadata: FieldMetaData[];
    };
    loadingFallback?: React.ReactNode;
    formValidationErrors?: { fieldName: string; messageText: string }[];
    readOnly?: boolean;
    additionalData?: Record<string, any>;
    hiddenFields?: Partial<Record<string, boolean>>;
    readOnlyFields?: Record<string, boolean>;
    fieldProps?: Record<string, any>;
    tooltipMainIconSource?: string;
    tooltipCloseIconSource?: string;
    renderTooltipTextAsHtml?: boolean;
}

const toCamelCase = (pascalCase: string): string => {
    return pascalCase.charAt(0).toLowerCase() + pascalCase.slice(1);
};

const formatFormFieldNames = (metaData: FieldMetaData[]): FieldMetaData[] => {
    return metaData.map((field) => ({
        ...field,
        name: toCamelCase(field.name),
    }));
};

export const FormBuilder = <T extends Record<string, any>>({
    fieldRenderers,
    overrideFormFieldRenderer,
    data,
    loadingFallback,
    formValidationErrors,
    readOnly = false,
    additionalData,
    hiddenFields,
    readOnlyFields,
    fieldProps,
    tooltipMainIconSource,
    tooltipCloseIconSource,
    renderTooltipTextAsHtml = false,
}: FormBuilderProps<T>) => {
    const { setErrors, isSubmitting, values } = useFormikContext<T>();

    const defaultRenderers: any = {
        text: (key: any, field: any, tooltipIconData: TooltipIconData) => (
            <FormTextField
                key={key}
                fieldName={field.name.toString()}
                label={field.label}
                readOnly={readOnly || (readOnlyFields?.[field.name] ?? false)}
                required={field.bRequired}
                inputProps={{
                    disabled: isSubmitting,
                    ...(fieldProps?.[field.name] || {}),
                }}
                tooltipIconData={tooltipIconData}
                tooltip={field.toolTip}
                renderTooltipTextAsHtml={renderTooltipTextAsHtml}
            />
        ),
        string: (key: any, field: FieldMetaData, tooltipIconData: TooltipIconData) => {
            return (
                <FormTextField
                    key={key}
                    fieldName={field.name.toString()}
                    label={field.label}
                    readOnly={readOnly || (readOnlyFields?.[field.name] ?? false)}
                    required={field.bRequired}
                    inputProps={{
                        disabled: isSubmitting,
                        ...(fieldProps?.[field.name] || {}),
                    }}
                    tooltipIconData={tooltipIconData}
                    tooltip={field.toolTip}
                    renderTooltipTextAsHtml={renderTooltipTextAsHtml}
                />
            );
        },
        textarea: (key: any, field: FieldMetaData, tooltipIconData: TooltipIconData) => (
            <FormTextAreaField
                key={key}
                fieldName={field.name.toString()}
                label={field.label}
                readOnly={readOnly || (readOnlyFields?.[field.name] ?? false)}
                required={field.bRequired}
                textAreaProps={{
                    disabled: isSubmitting,
                    ...(fieldProps?.[field.name] || {}),
                }}
                tooltipIconData={tooltipIconData}
                tooltip={field.toolTip}
                renderTooltipTextAsHtml={renderTooltipTextAsHtml}
            />
        ),
        number: (key: any, field: FieldMetaData, tooltipIconData: TooltipIconData) => (
            <FormTextField
                key={key}
                fieldName={field.name.toString()}
                label={field.label}
                readOnly={readOnly || (readOnlyFields?.[field.name] ?? false)}
                required={field.bRequired}
                inputProps={{
                    type: 'number', // Specify the input type as number
                    disabled: isSubmitting,
                    ...(fieldProps?.[field.name] || {}),
                }}
                tooltipIconData={tooltipIconData}
                tooltip={field.toolTip}
                renderTooltipTextAsHtml={renderTooltipTextAsHtml}
            />
        ),
        integer: (key: any, field: FieldMetaData, tooltipIconData: TooltipIconData) => {
            return (
                <FormTextField
                    key={key}
                    fieldName={field.name.toString()}
                    label={field.label}
                    readOnly={readOnly || (readOnlyFields?.[field.name] ?? false)}
                    required={field.bRequired}
                    inputProps={{
                        type: 'number', // Specify the input type as number
                        step: 1, // Ensure only integers are allowed
                        disabled: isSubmitting,
                        ...(fieldProps?.[field.name] || {}),
                    }}
                    tooltipIconData={tooltipIconData}
                    tooltip={field.toolTip}
                    renderTooltipTextAsHtml={renderTooltipTextAsHtml}
                />
            );
        },
        boolean: (key: any, field: FieldMetaData, tooltipIconData: TooltipIconData) => (
            <FormBooleanField
                key={key}
                fieldName={field.name.toString()}
                label={field.label}
                readOnly={readOnly || (readOnlyFields?.[field.name] ?? false)}
                required={field.bRequired}
                tooltipIconData={tooltipIconData}
                tooltip={field.toolTip}
                renderTooltipTextAsHtml={renderTooltipTextAsHtml}
            />
        ),
        datetime: (key: any, field: FieldMetaData, tooltipIconData: TooltipIconData) => (
            <FormDateTimeField
                key={key}
                fieldName={field.name.toString()}
                label={field.label}
                required={field.bRequired}
                readOnly={readOnly || (readOnlyFields?.[field.name] ?? false)}
                datePickerProps={{
                    disabled: isSubmitting,
                    ...(fieldProps?.[field.name] || {}),
                }}
                tooltipIconData={tooltipIconData}
                tooltip={field.toolTip}
                renderTooltipTextAsHtml={renderTooltipTextAsHtml}
            />
        ),
        singleselect: (key: any, field: FieldMetaData, tooltipIconData: TooltipIconData) => {
            const options = additionalData?.[field.name] || [];
            if (!options.length) {
                console.warn(`No options provided for single select field: ${field.name}`);
                return null;
            }

            return (
                <FormSingleSelectField
                    key={key}
                    fieldName={field.name.toString()}
                    label={field.label}
                    options={options}
                    readOnly={readOnly || (readOnlyFields?.[field.name] ?? false)}
                    required={field.bRequired}
                    dropdownProps={{
                        isDisabled: isSubmitting,
                        ...(fieldProps?.[field.name] || {}),
                    }}
                    tooltipIconData={tooltipIconData}
                    tooltip={field.toolTip}
                    renderTooltipTextAsHtml={renderTooltipTextAsHtml}
                />
            );
        },
        multiselect: (key: any, field: FieldMetaData, tooltipIconData: TooltipIconData) => {
            const options = additionalData?.[field.name] || [];
            if (!options.length) {
                console.warn(`No options provided for multi-select field: ${field.name}`);
                return null;
            }

            return (
                <FormMultiSelectField
                    key={key}
                    fieldName={field.name.toString()}
                    label={field.label}
                    options={options}
                    readOnly={readOnly || (readOnlyFields?.[field.name] ?? false)}
                    required={field.bRequired}
                    dropdownProps={{
                        isDisabled: isSubmitting,
                        ...(fieldProps?.[field.name] || {}),
                    }}
                    tooltipIconData={tooltipIconData}
                    tooltip={field.toolTip}
                    renderTooltipTextAsHtml={renderTooltipTextAsHtml}
                />
            );
        },
    };

    const renderers = { ...defaultRenderers, ...fieldRenderers };

    const tooltipIconData: TooltipIconData | null =
        tooltipMainIconSource && tooltipCloseIconSource
            ? { mainIcon: tooltipMainIconSource, closeIcon: tooltipCloseIconSource }
            : null;

    useEffect(() => {
        if (formValidationErrors?.length) {
            const errors: any = {};
            formValidationErrors.forEach((err) => {
                errors[toCamelCase(err.fieldName)] = err.messageText;
            });
            setErrors(errors);
        }
    }, [formValidationErrors, setErrors]);

    if (!data) {
        return <>{loadingFallback || <p>Loading...</p>}</>;
    }

    const metadata = data.metadata;
    if (!metadata) {
        throw new Error('No metadata field found on the provided data');
    }

    const formFields = formatFormFieldNames(metadata).filter((field) => {
        if (hiddenFields && hiddenFields[field.name]) {
            return false;
        }
        return field.type.toLowerCase() !== 'hidden';
    });

    const form = formFields.map((field, i) => {
        if (overrideFormFieldRenderer) {
            const rendererOverride = overrideFormFieldRenderer[field.name];
            if (rendererOverride) {
                return (
                    <React.Fragment key={field.name.toString()}>
                        {rendererOverride(i, field, tooltipIconData)}
                    </React.Fragment>
                );
            }
        }

        const renderer =
            renderers[field.type.toLowerCase()] ||
            ((i: any, field: any) => (
                <p key={i}>
                    Renderer for field type <strong>{field.type}</strong> is not defined
                </p>
            ));

        return (
            <React.Fragment key={field.name.toString()}>
                {renderer(i, field, tooltipIconData)}
            </React.Fragment>
        );
    });

    return <React.Fragment>{form}</React.Fragment>;
};
